import React, { useEffect, useState, useRef } from "react";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { motion, AnimatePresence } from "framer-motion";
import { api } from "@/lib/api";
import Sidebar from "@/components/Sidebar";
import WizardStepper from "@/components/WizardStepper";
import { ArrowLeft, ArrowRight, Save, Eye } from "lucide-react";

import { STEPS, emptyQuote, buildCityDatesMap, matchHotelToCity } from "./quotation/_shared";
import StepAgent from "./quotation/steps/StepAgent";
import StepLead from "./quotation/steps/StepLead";
import StepDestination from "./quotation/steps/StepDestination";
import StepCities from "./quotation/steps/StepCities";
import StepDates from "./quotation/steps/StepDates";
import StepFlights from "./quotation/steps/StepFlights";
import StepHotels from "./quotation/steps/StepHotels";
import StepItinerary from "./quotation/steps/StepItinerary";
import StepInclusions from "./quotation/steps/StepInclusions";
import StepExclusions from "./quotation/steps/StepExclusions";
import StepPolicies from "./quotation/steps/StepPolicies";
import StepPricing from "./quotation/steps/StepPricing";

export default function QuotationBuilder() {
  const nav = useNavigate();
  const { id } = useParams();
  const [urlParams] = useSearchParams();
  const initialTemplateFromUrl = urlParams.get("template");
  const [step, setStep] = useState(0);
  const [q, setQ] = useState({ ...emptyQuote, template_id: initialTemplateFromUrl || "classic" });
  const [saving, setSaving] = useState(false);
  const [qid, setQid] = useState(id || null);
  const [profile, setProfile] = useState(null);
  const [aiBusy, setAiBusy] = useState(false);
  const [aiIdx, setAiIdx] = useState(0);
  const skipAutosave = useRef(true);

  // Load profile + quotation
  useEffect(() => {
    (async () => {
      const [profRes] = await Promise.all([api.get("/profile")]);
      setProfile(profRes.data);
      if (id) {
        const { data } = await api.get(`/quotations/${id}`);
        setQ({ ...emptyQuote, ...data });
      } else if (!initialTemplateFromUrl && profRes.data?.default_template_id) {
        setQ((prev) => ({ ...prev, template_id: profRes.data.default_template_id }));
      }
      setTimeout(() => { skipAutosave.current = false; }, 800);
    })();
  }, [id]);

  // Auto-calc dates & totals
  useEffect(() => {
    const totalNights = (q.cities || []).reduce((s, c) => s + Number(c.nights || 0), 0);
    const totalDays = totalNights > 0 ? totalNights + 1 : 0;
    let endDate = q.end_date;
    if (q.start_date && totalNights >= 0) {
      const d = new Date(q.start_date);
      d.setDate(d.getDate() + totalNights);
      endDate = d.toISOString().split("T")[0];
    }
    if (totalNights !== q.total_nights || totalDays !== q.total_days || endDate !== q.end_date) {
      setQ((p) => ({ ...p, total_nights: totalNights, total_days: totalDays, end_date: endDate }));
    }
  }, [q.cities, q.start_date]); // eslint-disable-line

  // Auto-calculate hotel check-in / check-out from Package Start Date + city nights.
  useEffect(() => {
    if (!q.auto_hotel_dates || !q.start_date) return;
    const cityMap = buildCityDatesMap(q.start_date, q.cities);
    if (!Object.keys(cityMap).length) return;
    let changed = false;
    const nextHotels = (q.hotels || []).map((h) => {
      const info = matchHotelToCity(h, cityMap);
      if (!info) return h;
      if (h.check_in === info.check_in && h.check_out === info.check_out && Number(h.nights) === info.nights) return h;
      changed = true;
      return { ...h, check_in: info.check_in, check_out: info.check_out, nights: info.nights };
    });
    if (changed) setQ((p) => ({ ...p, hotels: nextHotels }));
  }, [
    q.auto_hotel_dates, q.start_date,
    JSON.stringify(q.cities),
    JSON.stringify((q.hotels || []).map(h => ({ n: h.name, a: h.address, c: h.city }))),
  ]); // eslint-disable-line

  // Auto-calc pricing grand total
  useEffect(() => {
    const p = q.pricing;
    const gt = (Number(p.package_cost)||0) + (Number(p.taxes)||0) + (Number(p.tcs)||0) +
               (Number(p.visa_charges)||0) + (Number(p.markup)||0) + (Number(p.service_charges)||0) -
               (Number(p.discount)||0);
    if (gt !== p.grand_total) {
      setQ((prev) => ({ ...prev, pricing: { ...prev.pricing, grand_total: gt } }));
    }
  }, [q.pricing.package_cost, q.pricing.taxes, q.pricing.tcs, q.pricing.visa_charges,
      q.pricing.markup, q.pricing.service_charges, q.pricing.discount]); // eslint-disable-line

  // Autosave (debounced)
  useEffect(() => {
    if (skipAutosave.current) return;
    const t = setTimeout(() => { save(false); }, 1500);
    return () => clearTimeout(t);
    // eslint-disable-next-line
  }, [q]);

  const save = async (notify = true) => {
    setSaving(true);
    try {
      if (qid) {
        await api.put(`/quotations/${qid}`, q);
      } else {
        const { data } = await api.post("/quotations", q);
        setQid(data.id);
        setQ((p) => ({ ...p, quotation_number: data.quotation_number }));
        window.history.replaceState(null, "", `/quotations/${data.id}/edit`);
      }
      if (notify) toast.success("Draft saved");
    } catch (e) {
      if (notify) toast.error("Save failed");
    } finally { setSaving(false); }
  };

  const genDestAI = async (idx = 0) => {
    const dests = (q.destinations && q.destinations.length) ? q.destinations : [q.destination];
    const target = dests[idx] || {};
    if (!target.country) return toast.error("Pick a country first");
    setAiIdx(idx); setAiBusy(true);
    try {
      const { data } = await api.post("/ai/destination", { country: target.country });
      const nextDests = dests.map((d, i) => i === idx ? { ...d, ...data } : d);
      setQ((p) => ({ ...p, destinations: nextDests, destination: nextDests[0] }));
      toast.success("AI content generated");
    } catch (e) { toast.error("AI generation failed"); }
    finally { setAiBusy(false); }
  };

  const genItinAI = async () => {
    if (!q.destination.country || !q.cities.length) return toast.error("Country & cities required");
    setAiBusy(true);
    try {
      const { data } = await api.post("/ai/itinerary", {
        country: q.destination.country,
        cities: q.cities,
        total_days: q.total_days || 1,
        theme: q.trip_theme || "",
      });
      const days = data.days.map((d, i) => ({
        ...d, day: i + 1,
        date: q.start_date ? new Date(new Date(q.start_date).getTime() + i*86400000).toISOString().split("T")[0] : "",
        meal_plan: d.meal_plan || "", hotel: "", sightseeing: d.sightseeing || "", images: [], notes: "",
      }));
      setQ((p) => ({ ...p, itinerary: days }));
      toast.success("Itinerary generated");
    } catch (e) { toast.error("AI itinerary failed"); }
    finally { setAiBusy(false); }
  };

  // Pull policies from profile if empty
  useEffect(() => {
    if (!profile) return;
    if (!q.policies.payment_policy && !q.policies.cancellation_policy && !q.policies.terms_conditions) {
      setQ((p) => ({ ...p, policies: {
        payment_policy: profile.payment_policy || "",
        cancellation_policy: profile.cancellation_policy || "",
        terms_conditions: profile.terms_conditions || "",
      }}));
    }
    // eslint-disable-next-line
  }, [profile]);

  return (
    <div className="flex min-h-screen">
      <Sidebar role="agent" />
      <main className="flex-1 bg-[#FCFCFC] p-6 lg:p-10">
        <div className="flex items-start gap-8 max-w-[1400px]">
          {/* Left stepper */}
          <div className="w-72 shrink-0">
            <WizardStepper steps={STEPS} current={step} onGo={setStep} />
            <div className="mt-4 space-y-2">
              <button data-testid="wizard-save-btn" onClick={() => save(true)} disabled={saving} className="w-full inline-flex items-center justify-center gap-2 py-2 border border-slate-300 rounded text-xs uppercase tracking-widest hover:border-[#16A34A]">
                <Save size={13} /> {saving ? "Saving…" : "Save draft"}
              </button>
              {qid && (
                <button data-testid="wizard-preview-btn" onClick={() => { save(false); nav(`/quotations/${qid}/preview`); }} className="w-full inline-flex items-center justify-center gap-2 py-2 border border-slate-300 rounded text-xs uppercase tracking-widest hover:border-[#16A34A]">
                  <Eye size={13} /> Preview
                </button>
              )}
            </div>
            <div className="text-xs text-slate-400 mt-4">
              {q.quotation_number && <>#{q.quotation_number}</>}
            </div>
          </div>

          {/* Content */}
          <div className="flex-1 min-w-0">
            <div className="flex items-center justify-between mb-6">
              <div>
                <div className="overline">Step {step+1} of 12</div>
                <h1 className="font-editorial text-3xl mt-1 text-slate-900">{STEPS[step]}</h1>
              </div>
              <div className="flex items-center gap-2">
                <button data-testid="wizard-back-btn" onClick={()=>setStep(Math.max(0, step-1))} disabled={step===0} className="inline-flex items-center gap-1 text-xs uppercase tracking-widest px-3 py-2 border border-border rounded disabled:opacity-30"><ArrowLeft size={13}/> Back</button>
                <button data-testid="wizard-next-btn" onClick={()=>setStep(Math.min(STEPS.length-1, step+1))} disabled={step===STEPS.length-1} className="btn-bronze inline-flex items-center gap-1"><span className="text-xs uppercase tracking-widest">Next</span> <ArrowRight size={13}/></button>
              </div>
            </div>

            <AnimatePresence mode="wait">
              <motion.div key={step}
                initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -6 }}
                transition={{ duration: 0.22 }}
                className="bg-white border border-border rounded p-8">
                {step === 0 && <StepAgent profile={profile} onEdit={() => nav("/profile/setup")} />}
                {step === 1 && <StepLead q={q} setQ={setQ} />}
                {step === 2 && <StepDestination q={q} setQ={setQ} onAI={genDestAI} aiIdx={aiIdx} aiBusy={aiBusy} />}
                {step === 3 && <StepCities q={q} setQ={setQ} />}
                {step === 4 && <StepDates q={q} setQ={setQ} />}
                {step === 5 && <StepFlights q={q} setQ={setQ} />}
                {step === 6 && <StepHotels q={q} setQ={setQ} cityMap={buildCityDatesMap(q.start_date, q.cities)} />}
                {step === 7 && <StepItinerary q={q} setQ={setQ} onAI={genItinAI} aiBusy={aiBusy} />}
                {step === 8 && <StepInclusions q={q} setQ={setQ} />}
                {step === 9 && <StepExclusions q={q} setQ={setQ} />}
                {step === 10 && <StepPolicies q={q} setQ={setQ} />}
                {step === 11 && <StepPricing q={q} setQ={setQ} qid={qid} save={save} />}
              </motion.div>
            </AnimatePresence>

            {/* Bottom navigation (mirrors top-right so agents don't scroll up) */}
            <div className="mt-6 flex flex-wrap items-center justify-end gap-2">
              <button
                data-testid="wizard-back-btn-bottom"
                onClick={() => { setStep(Math.max(0, step - 1)); window.scrollTo({ top: 0, behavior: "smooth" }); }}
                disabled={step === 0}
                className="inline-flex items-center gap-1 text-xs uppercase tracking-widest px-3 py-2 border border-border rounded disabled:opacity-30"
              >
                <ArrowLeft size={13} /> Back
              </button>
              {step < STEPS.length - 1 && (
                <button
                  data-testid="wizard-next-btn-bottom"
                  onClick={() => { setStep(Math.min(STEPS.length - 1, step + 1)); window.scrollTo({ top: 0, behavior: "smooth" }); }}
                  className="btn-bronze inline-flex items-center gap-1"
                >
                  <span className="text-xs uppercase tracking-widest">Next</span> <ArrowRight size={13} />
                </button>
              )}
            </div>
          </div>
        </div>
      </main>
    </div>
  );
}
